import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, HistorialPedidoCard } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF, guardarPDF, agruparPorMaterial } from "./helpers";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters, sortPendingFirst } from "./BusquedaFilters";
import { ocInvoiceComplete } from "../../utils/invoices";
import { roleAllowed } from "../../utils/roles";
import { ocInvoices, ocCoverage, ocInvoiceLabel } from "../../utils/invoices";

export function OrdenesTab({
  ocFromPF, finalBudgets, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [showNew, setShowNew] = useState(false);
  const [selectedPFForOC, setSelectedPFForOC] = useState(null);
  const [selectedPFIds, setSelectedPFIds] = useState([]);
  const [form, setForm] = useState({ sucursal: "", condicion_pago: "", notes: "" });
  const [generating, setGenerating] = useState(false);
  const [viewOC, setViewOC] = useState(null);
  const [editOC, setEditOC] = useState(null);
  const [editData, setEditData] = useState({ sucursal: "", condicion_pago: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [approvalLimit, setApprovalLimit] = useState(50_000_000);
  const [editLimit, setEditLimit] = useState(false);
  const [newLimit, setNewLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  // Provider sucursales lookup (obs 5)
  const [listasProveedores, setListasProveedores] = useState([]);
  const [addingSucursal, setAddingSucursal] = useState(false);
  const [newSucursalName, setNewSucursalName] = useState("");
  // Condiciones de pago (obs PDF pág 6: deben venir del proveedor en Listas)
  const [addingCondicion, setAddingCondicion] = useState(false);
  const [newCondicionName, setNewCondicionName] = useState("");
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");

  useEffect(() => {
    axios.get(`${API}/listas/proveedores`, { withCredentials: true })
      .then(r => setListasProveedores(r.data || []))
      .catch(() => {});
  }, []);

  // Find provider's sucursales by name (case-insensitive)
  const findProvSucursales = (provName) => {
    if (!provName) return { prov: null, sucursales: [] };
    const norm = provName.trim().toLowerCase();
    const prov = listasProveedores.find(p =>
      (p.proveedor || "").trim().toLowerCase() === norm ||
      (p.proveedor || "").trim().toLowerCase().includes(norm) ||
      norm.includes((p.proveedor || "").trim().toLowerCase())
    );
    return { prov, sucursales: (prov?.sucursales || []).filter(Boolean) };
  };

  // Find provider's condiciones_pago by name (case-insensitive)
  const findProvCondiciones = (provName) => {
    if (!provName) return { prov: null, condiciones: [] };
    const norm = provName.trim().toLowerCase();
    const prov = listasProveedores.find(p =>
      (p.proveedor || "").trim().toLowerCase() === norm ||
      (p.proveedor || "").trim().toLowerCase().includes(norm) ||
      norm.includes((p.proveedor || "").trim().toLowerCase())
    );
    return { prov, condiciones: (prov?.condiciones_pago || []).filter(Boolean) };
  };

  const addCondicionToProv = async (provName, condicionName, onSuccess) => {
    const cn = (condicionName || "").trim();
    if (!cn) { toast.error("Ingrese una condición de pago"); return; }
    const { prov, condiciones } = findProvCondiciones(provName);
    if (!prov) { toast.error("Proveedor no está en el catálogo Listas"); return; }
    if (condiciones.map(s => s.toLowerCase()).includes(cn.toLowerCase())) {
      toast.info("La condición de pago ya existe en el proveedor");
      return;
    }
    try {
      const updated = {
        proveedor: prov.proveedor || "",
        rubro: prov.rubro || "",
        sucursales: prov.sucursales || [],
        condiciones_pago: [...condiciones, cn],
        celular: prov.celular || "",
        email: prov.email || "",
        ruc: prov.ruc || "",
      };
      await axios.put(`${API}/listas/proveedores/${prov._id || prov.id}`, updated, { withCredentials: true });
      toast.success("Condición de pago agregada al proveedor");
      const r = await axios.get(`${API}/listas/proveedores`, { withCredentials: true });
      setListasProveedores(r.data || []);
      if (onSuccess) onSuccess(cn);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al agregar condición"); }
  };

  const addSucursalToProv = async (provName, sucursalName, onSuccess) => {
    const sn = (sucursalName || "").trim();
    if (!sn) { toast.error("Ingrese una sucursal"); return; }
    const { prov, sucursales } = findProvSucursales(provName);
    if (!prov) { toast.error("Proveedor no está en el catálogo Listas"); return; }
    if (sucursales.map(s => s.toLowerCase()).includes(sn.toLowerCase())) {
      toast.info("La sucursal ya existe en el proveedor");
      return;
    }
    try {
      const updated = { ...prov, sucursales: [...sucursales, sn] };
      delete updated._id;
      await axios.put(`${API}/listas/proveedores/${prov._id || prov.id}`, updated, { withCredentials: true });
      toast.success("Sucursal agregada al proveedor");
      const r = await axios.get(`${API}/listas/proveedores`, { withCredentials: true });
      setListasProveedores(r.data || []);
      if (onSuccess) onSuccess(sn);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al agregar sucursal"); }
  };

  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canApprove = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico"]);
  const canSetLimit = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico"]);

  // PFs not yet converted to OC
  const availablePFs = finalBudgets.filter(pf => pf.status !== "oc_generada" && pf.status !== "facturada");
  const baseFilteredOCs = ocFromPF.filter(oc => matchClienteObraPresupuesto(oc, { filterCliente, filterObra, filterPresupuesto }, { listasClientes, listasObras }));
  const ocStatusCounts = baseFilteredOCs.reduce((acc, oc) => { acc[oc.status] = (acc[oc.status] || 0) + 1; return acc; }, {});
  const filteredOCsUnsorted = baseFilteredOCs.filter(oc => {
    if (filterStatus !== "todos" && oc.status !== filterStatus) return false;
    if (filterUrgency && oc.urgency !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (oc.number || "").toLowerCase().includes(s) || (oc.project_name || "").toLowerCase().includes(s) ||
        (oc.provider_name || "").toLowerCase().includes(s) || (oc.pf_numbers || []).some(n => (n || "").toLowerCase().includes(s));
      if (!hit) return false;
    }
    return true;
  });
  // BLOQUE 50 PARTE B — OC sin factura / pendientes de facturar arriba; facturadas y entregadas abajo; más nueva primero.
  const filteredOCs = sortPendingFirst(filteredOCsUnsorted, oc => oc.status !== "cancelada" && !ocInvoiceComplete(oc));

  useEffect(() => {
    axios.get(`${API}/purchases/oc-approval-limit`, { withCredentials: true })
      .then(r => setApprovalLimit(r.data.limit_pyg))
      .catch(() => {});
  }, []);

  // Open inline OC generation modal for a specific PF
  const openInlineOC = (pf) => {
    setSelectedPFForOC(pf);
    setForm({
      sucursal: pf.sucursal || "",
      condicion_pago: pf.condicion_pago || "",
      notes: ""
    });
    setShowNew(true);
  };

  const togglePF = (id) => {
    setSelectedPFIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleGenerate = async () => {
    const pfIds = selectedPFForOC ? [selectedPFForOC._id || selectedPFForOC.id] : selectedPFIds;
    if (!pfIds.length) { toast.error("Seleccione al menos un PF"); return; }
    setGenerating(true);
    try {
      await axios.post(`${API}/purchases/oc-from-pf/generate`, { pf_ids: pfIds, ...form }, { withCredentials: true });
      toast.success("Orden de Compra generada exitosamente");
      setShowNew(false);
      setSelectedPFForOC(null);
      setSelectedPFIds([]);
      setForm({ sucursal: "", condicion_pago: "", notes: "" });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar OC"); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/purchases/oc-from-pf/${id}/approve`, {}, { withCredentials: true });
      toast.success("OC aprobada");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("¿Cancelar esta OC?")) return;
    try {
      await axios.put(`${API}/purchases/oc-from-pf/${id}/cancel`, {}, { withCredentials: true });
      toast.success("OC cancelada");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleSaveEdit = async () => {
    if (!editOC) return;
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/oc-from-pf/${editOC._id}/update`, editData, { withCredentials: true });
      toast.success("OC actualizada");
      setEditOC(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleSaveLimit = async () => {
    const val = Number(newLimit);
    if (!val || val <= 0) { toast.error("Ingrese un monto válido"); return; }
    setSavingLimit(true);
    try {
      const r = await axios.put(`${API}/purchases/oc-approval-limit`, { limit_pyg: val }, { withCredentials: true });
      setApprovalLimit(r.data.limit_pyg);
      setEditLimit(false);
      setNewLimit("");
      toast.success("Límite actualizado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSavingLimit(false); }
  };

  const generateOCPDF = (oc) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pw = doc.internal.pageSize.getWidth();
    const CEN_BLUE = [46, 109, 164];
    doc.setFillColor(...CEN_BLUE);
    doc.rect(0, 0, pw, 30, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(255, 255, 255);
    doc.text("CEN INGENIERIA E.A.S.", 14, 11);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text("Instalaciones Eléctricas — Asunción, Paraguay", 14, 17);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("ORDEN DE COMPRA", pw - 14, 11, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(oc.number || "", pw - 14, 18, { align: "right" });

    let y = 40;
    doc.setFontSize(8.5); doc.setTextColor(30, 30, 30);
    const fieldRow = (x, fy, label, value) => {
      doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 80);
      doc.text(label + ":", x, fy);
      doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20);
      doc.text(String(value || "—").substring(0, 55), x + 34, fy);
    };
    const c2 = pw / 2 + 4;
    fieldRow(14, y, "Proyecto / Obra", oc.project_name); fieldRow(c2, y, "Proveedor", oc.provider_name);
    fieldRow(14, y + 7, "Sucursal de retiro", oc.sucursal || "—"); fieldRow(c2, y + 7, "Cond. de pago", oc.condicion_pago || "—");
    fieldRow(14, y + 14, "PF(s) Origen", (oc.pf_numbers || []).join(", ")); fieldRow(c2, y + 14, "Estado", STATUS_LABELS[oc.status] || oc.status);

    doc.setDrawColor(...CEN_BLUE); doc.setLineWidth(0.3); doc.line(14, y + 20, pw - 14, y + 20);
    y += 26;
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_BLUE);
    doc.text("ÍTEMS DE LA ORDEN", 14, y);

    autoTable(doc, {
      startY: y + 3,
      head: [["Descripción", "UM", "Cant.", "P.U. c/IVA (Gs.)", "Subtotal c/IVA (Gs.)"]],
      body: agruparPorMaterial(oc.items, true).map(item => {  // Fix EG (2026-09-25)
        // item.unit_price y item.subtotal ya vienen con IVA incluido (convención CEN)
        const upWith = Math.round(item.unit_price || 0);
        const subWith = Math.round(item.subtotal || 0);
        return [
          item.item_name || item.material_name || "—",
          item.unit || "un",
          String(item.quantity ?? "—"),
          formatPYG(upWith),
          formatPYG(subWith),
        ];
      }),
      headStyles: { fillColor: CEN_BLUE, textColor: [255, 255, 255], fontSize: 8.5, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [237, 245, 255] },
      columnStyles: { 0: { cellWidth: 75 }, 1: { cellWidth: 12 }, 2: { cellWidth: 14 }, 3: { cellWidth: 32 }, 4: { cellWidth: 38 } },
      margin: { left: 14, right: 14 }
    });

    const totY = doc.lastAutoTable.finalY + 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...CEN_BLUE);
    doc.text(`TOTAL (IVA incluido): ${formatPYG(oc.total || 0)}`, pw - 14, totY + 4, { align: "right" });

    // BLOQUE 41 — listado de TODAS las facturas vinculadas a la OC
    const invs = ocInvoices(oc);
    if (invs.length > 0) {
      const cov = ocCoverage(oc);
      let iy = totY + 14;
      doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_BLUE);
      doc.text(`FACTURAS VINCULADAS (${invs.length})`, 14, iy);
      autoTable(doc, {
        startY: iy + 3,
        head: [["N° Factura", "Cód. interno", "Fecha", "Timbrado", "Monto (Gs.)"]],
        body: invs.map(inv => [
          inv.invoice_number || "—",
          inv.fac_number || "—",
          inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : "—",
          inv.timbrado || "—",
          formatPYG(inv.invoice_total || 0),
        ]),
        foot: [["", "", "", "TOTAL FACTURADO", formatPYG(cov.invoiced_total || 0)]],
        headStyles: { fillColor: [16, 122, 87], textColor: [255, 255, 255], fontSize: 8.5, fontStyle: "bold" },
        footStyles: { fillColor: [235, 245, 240], textColor: [30, 30, 30], fontSize: 8.5, fontStyle: "bold" },
        bodyStyles: { fontSize: 8.5 },
        columnStyles: { 0: { cellWidth: 42 }, 1: { cellWidth: 42 }, 2: { cellWidth: 26 }, 3: { cellWidth: 28 }, 4: { cellWidth: 35, halign: "right" } },
        margin: { left: 14, right: 14 }
      });
    }

    const footerY = doc.internal.pageSize.getHeight() - 8;
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
    doc.text(`Generado por Sistema ERP CEN · ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });
    guardarPDF(doc, `${oc.number || "oc"}.pdf`);
  };


  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">{filteredOCs.length} de {ocFromPF.length} órdenes de compra</p>
        <div className="flex items-center gap-2 flex-wrap">
          <FiltroClienteObraPresupuesto
            listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
            filterCliente={filterCliente} setFilterCliente={setFilterCliente}
            filterObra={filterObra} setFilterObra={setFilterObra}
            filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
            testIdPrefix="ordenes"
          />
          <BusquedaEstadoUrgenciaFilters
            search={search} setSearch={setSearch}
            searchPlaceholder="Buscar por N° OC, PF, obra o proveedor..."
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            statusOptions={[
              { value: "todos", label: `Todos (${baseFilteredOCs.length})` },
              { value: "borrador", label: `Borrador (${ocStatusCounts.borrador || 0})` },
              { value: "pendiente_aprobacion", label: `Pend. Aprobación (${ocStatusCounts.pendiente_aprobacion || 0})` },
              { value: "aprobada", label: `Aprobada (${ocStatusCounts.aprobada || 0})` },
              { value: "facturada_parcial", label: `Facturada Parcial (${ocStatusCounts.facturada_parcial || 0})` },
              { value: "facturada", label: `Facturada (${ocStatusCounts.facturada || 0})` },
              { value: "retiro_materiales", label: `En Retiro (${ocStatusCounts.retiro_materiales || 0})` },
              { value: "entregado", label: `Entregado (${ocStatusCounts.entregado || 0})` },
              { value: "cancelada", label: `Cancelada (${ocStatusCounts.cancelada || 0})` },
            ]}
            filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
            testIdPrefix="ordenes"
          />
          {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
            <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
              data-testid="ordenes-clear-filter">
              <X size={12} /> Limpiar
            </button>
          )}
          {canSetLimit && (
            <button onClick={() => { setEditLimit(true); setNewLimit(String(approvalLimit)); }}
              className="flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-xs font-medium transition-all"
              data-testid="set-limit-btn">
              Límite aprobación
            </button>
          )}
        </div>
      </div>

      {/* PENDIENTES: PFs sin OC — lista inline */}
      {canCreate && availablePFs.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-blue-100/70 border-b border-blue-200 flex items-center gap-2">
            <ShoppingCart size={13} className="text-[#2E6DA4]" />
            <p className="text-xs font-bold text-[#1E3A5F]">Presupuestos Finales pendientes de OC — {availablePFs.length}</p>
          </div>
          <div className="divide-y divide-blue-100/60">
            {availablePFs.map(pf => (
              <div key={pf._id || pf.id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-blue-50/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold text-gray-900 font-mono">{pf.number}</span>
                  <span className="text-xs text-gray-600 ml-2 font-medium">{pf.provider_name}</span>
                  <span className="text-xs text-gray-400 ml-2">{pf.project_name}</span>
                  {pf.total > 0 && <span className="text-xs font-bold text-[#2E6DA4] ml-2">{formatPYG(pf.total)}</span>}
                </div>
                <button
                  onClick={() => openInlineOC(pf)}
                  disabled={generating}
                  className="flex items-center gap-1 text-xs bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-semibold flex-shrink-0 transition-all"
                  data-testid={`generar-oc-inline-${pf._id || pf.id}`}>
                  <ShoppingCart size={11} /> Generar OC
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-[#2E6DA4]/8 rounded-xl border border-[#2E6DA4]/20 p-4 text-xs text-[#2E6DA4]">
        <p className="font-semibold mb-1">Órdenes de Compra (OC)</p>
        <p className="text-gray-600">Generadas desde Presupuestos Finales (PF). Monto bajo el límite (<span className="font-bold">{formatPYG(approvalLimit)} Gs</span>) se aprueba automáticamente. Montos superiores requieren aprobación del Gerente Técnico.</p>
      </div>

      {/* OC List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}</div>
      ) : filteredOCs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <ShoppingCart size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay órdenes de compra</p>
          <p className="text-xs text-gray-400 mt-1">
            {availablePFs.length > 0 ? "Genere una OC seleccionando Presupuestos Finales" : "Primero genere Presupuestos Finales desde el AP"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOCs.map(oc => (
            <div key={oc._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="oc-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900 text-sm font-mono">{oc.number}</p>
                    <StatusBadge status={oc.status} />
                    {oc.match_result === "ai_matched" && (
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <BadgeCheck size={10} /> Factura vinculada
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 font-medium mt-1">{oc.project_name} · {oc.provider_name}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-sm font-bold text-[#2E6DA4]">{formatPYG(oc.total || 0)} <span className="text-[10px] text-gray-400 font-normal">c/IVA</span></span>
                  </div>
                  {oc.pf_numbers?.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {oc.pf_numbers.map((pn, i) => (
                        <span key={i} className="text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full font-mono">{pn}</span>
                      ))}
                    </div>
                  )}
                  {oc.sucursal && <p className="text-xs text-gray-400 mt-0.5">Sucursal: {oc.sucursal} {oc.condicion_pago ? `· ${oc.condicion_pago}` : ""}</p>}
                  {(() => {
                    const invs = ocInvoices(oc);
                    const cov = ocCoverage(oc);
                    const showPendingBadge = cov.pending_total > 0 && !["borrador", "pendiente_aprobacion", "cancelada"].includes(oc.status);
                    return (
                      <>
                        {invs.length > 0 && (
                          <p className="text-xs text-emerald-600 font-medium mt-0.5" data-testid={`oc-card-invoices-${oc._id}`}>
                            Factura{invs.length > 1 ? "s" : ""}: {ocInvoiceLabel(oc)}
                            {invs[0]?.fac_number && <span className="text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded ml-1 font-mono">{invs[0].fac_number}</span>}
                            {" · "}{formatPYG(cov.invoiced_total || 0)} Gs
                            {invs.length > 1 ? ` (${invs.length} facturas)` : ""}
                          </p>
                        )}
                        {showPendingBadge && (
                          <p className="text-xs text-amber-700 font-bold mt-0.5" data-testid={`oc-card-falta-facturar-${oc._id}`}>
                            Facturado {formatPYG(cov.invoiced_total || 0)} · Falta facturar {formatPYG(cov.pending_total)}
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {oc.payment_order_number && (
                    <p className={`text-xs font-medium mt-0.5 flex items-center gap-1 ${oc.payment_status === "pagada" ? "text-emerald-600" : "text-amber-600"}`} data-testid={`oc-payment-status-${oc._id}`}>
                      <Banknote size={11} />
                      OP: {oc.payment_order_number} {oc.payment_status === "pagada" ? "(Pagada)" : "(Pendiente de pago)"}
                    </p>
                  )}
                  {oc.baja_tesoreria && !oc.payment_order_number && (   /* Fix IL: dada de baja al rechazar su OP en Tesorería */
                    <p className="text-xs font-medium mt-0.5 flex items-center gap-1 text-gray-500" data-testid={`oc-baja-tesoreria-${oc._id}`} title={oc.baja_tesoreria.motivo || ""}>
                      <Banknote size={11} />
                      Dada de baja en Tesorería ({oc.baja_tesoreria.op_number}){oc.baja_tesoreria.motivo ? `: ${oc.baja_tesoreria.motivo}` : ""}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">{formatDate(oc.created_at)} · {oc.created_by_name}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {oc.status === "pendiente_aprobacion" && canApprove && (
                    <button onClick={() => handleApprove(oc._id)}
                      className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1.5 rounded-lg hover:bg-emerald-200 font-semibold transition-colors"
                      data-testid="approve-oc-btn">
                      Aprobar
                    </button>
                  )}
                  <button onClick={() => { setEditOC(oc); setEditData({ sucursal: oc.sucursal || "", condicion_pago: oc.condicion_pago || "", notes: oc.notes || "" }); }}
                    title="Editar" className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                    data-testid="edit-oc-btn">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => setViewOC(oc)} title="Ver detalle"
                    className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                    data-testid="view-oc-btn">
                    <Eye size={15} />
                  </button>
                  <button onClick={() => generateOCPDF(oc)} title="Descargar PDF"
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="pdf-oc-btn">
                    <Download size={15} />
                  </button>
                  {["borrador", "pendiente_aprobacion"].includes(oc.status) && canCreate && (
                    <button onClick={() => handleCancel(oc._id)} title="Cancelar"
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                      data-testid="cancel-oc-btn">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Generar OC desde PF */}
      <Modal open={showNew} onClose={() => { setShowNew(false); setSelectedPFForOC(null); setSelectedPFIds([]); setForm({ sucursal: "", condicion_pago: "", notes: "" }); }} title={selectedPFForOC ? `Generar OC: ${selectedPFForOC.number}` : "Nueva Orden de Compra"}>
        <div className="space-y-4">
          {selectedPFForOC && (
            <div className="bg-blue-50 rounded-xl border border-blue-200 px-4 py-3">
              <p className="text-xs font-bold text-[#1E3A5F]">{selectedPFForOC.number}</p>
              <p className="text-xs text-gray-600">{selectedPFForOC.project_name} · {selectedPFForOC.provider_name}</p>
              {selectedPFForOC.total > 0 && <p className="text-sm font-bold text-[#2E6DA4] mt-1">{formatPYG(selectedPFForOC.total)}</p>}
              {selectedPFForOC.total > approvalLimit && (
                <p className="text-[10px] text-amber-600 font-semibold mt-1">* Requiere aprobación (supera límite)</p>
              )}
              {(selectedPFForOC.sucursal || selectedPFForOC.condicion_pago) && (
                <div className="mt-2 flex items-start gap-1.5" data-testid="oc-autofill-banner">
                  <CheckCheck size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-emerald-700 leading-tight">
                    Sucursal y condición de pago precargados desde el PF. Podés modificarlos antes de confirmar.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sucursal de retiro</label>
              {(() => {
                const provName = selectedPFForOC?.provider_name || "";
                const { prov, sucursales } = findProvSucursales(provName);
                if (sucursales.length > 0 && !addingSucursal) {
                  return (
                    <div className="space-y-1.5">
                      <select value={form.sucursal} onChange={e => {
                          if (e.target.value === "__add__") { setAddingSucursal(true); setNewSucursalName(""); return; }
                          setForm(p => ({ ...p, sucursal: e.target.value }));
                        }}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                        data-testid="oc-sucursal-select">
                        <option value="">Seleccionar sucursal del proveedor...</option>
                        {sucursales.map((s, i) => <option key={i} value={s}>{s}</option>)}
                        <option value="__add__">+ Agregar nueva sucursal</option>
                      </select>
                      <p className="text-[10px] text-gray-400">Desplegado desde el catálogo de proveedores ({prov?.codigo})</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      <input value={addingSucursal ? newSucursalName : form.sucursal}
                        onChange={e => addingSucursal ? setNewSucursalName(e.target.value) : setForm(p => ({ ...p, sucursal: e.target.value }))}
                        placeholder="Ej: ASUNCIÓN, AVIADORES"
                        className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                        data-testid="oc-sucursal-input" />
                      {addingSucursal && prov && (
                        <button type="button"
                          onClick={() => addSucursalToProv(provName, newSucursalName, (sn) => {
                            setForm(p => ({ ...p, sucursal: sn })); setAddingSucursal(false); setNewSucursalName("");
                          })}
                          className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold"
                          data-testid="save-new-sucursal-btn">
                          <Check size={13} />
                        </button>
                      )}
                      {addingSucursal && (
                        <button type="button" onClick={() => { setAddingSucursal(false); setNewSucursalName(""); }}
                          className="px-3 py-2 border border-gray-300 text-gray-600 rounded-xl text-xs">
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    {!prov && provName && (
                      <p className="text-[10px] text-amber-600">Este proveedor no está en el catálogo Listas — la sucursal se guardará solo en esta OC.</p>
                    )}
                    {prov && sucursales.length === 0 && !addingSucursal && (
                      <p className="text-[10px] text-gray-400">Sin sucursales registradas. <button type="button" onClick={() => { setAddingSucursal(true); setNewSucursalName(form.sucursal || ""); }} className="text-[#2E6DA4] font-semibold hover:underline">+ Agregar al catálogo</button></p>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Condición de pago</label>
              {(() => {
                const provName = selectedPFForOC?.provider_name || "";
                const { prov, condiciones } = findProvCondiciones(provName);
                if (condiciones.length > 0 && !addingCondicion) {
                  return (
                    <div className="space-y-1.5">
                      <select value={form.condicion_pago} onChange={e => {
                          if (e.target.value === "__add__") { setAddingCondicion(true); setNewCondicionName(""); return; }
                          setForm(p => ({ ...p, condicion_pago: e.target.value }));
                        }}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                        data-testid="oc-condicion-input">
                        <option value="">Seleccionar condición del proveedor...</option>
                        {condiciones.map((c, i) => <option key={i} value={c}>{c}</option>)}
                        <option value="__add__">+ Agregar nueva condición</option>
                      </select>
                      <p className="text-[10px] text-gray-400">Desplegado desde el catálogo de proveedores ({prov?.codigo})</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      <input value={addingCondicion ? newCondicionName : form.condicion_pago}
                        onChange={e => addingCondicion ? setNewCondicionName(e.target.value) : setForm(p => ({ ...p, condicion_pago: e.target.value }))}
                        placeholder="Ej: Contado, 30 días, 30/60/90 días"
                        className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                        data-testid="oc-condicion-input" />
                      {addingCondicion && prov && (
                        <button type="button"
                          onClick={() => addCondicionToProv(provName, newCondicionName, (cn) => {
                            setForm(p => ({ ...p, condicion_pago: cn })); setAddingCondicion(false); setNewCondicionName("");
                          })}
                          className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold"
                          data-testid="save-new-condicion-btn">
                          <Check size={13} />
                        </button>
                      )}
                      {addingCondicion && (
                        <button type="button" onClick={() => { setAddingCondicion(false); setNewCondicionName(""); }}
                          className="px-3 py-2 border border-gray-300 text-gray-600 rounded-xl text-xs">
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    {!prov && provName && (
                      <p className="text-[10px] text-amber-600">Este proveedor no está en el catálogo Listas — la condición se guardará solo en esta OC.</p>
                    )}
                    {prov && condiciones.length === 0 && !addingCondicion && (
                      <p className="text-[10px] text-gray-400">Sin condiciones registradas. <button type="button" onClick={() => { setAddingCondicion(true); setNewCondicionName(form.condicion_pago || ""); }} className="text-[#2E6DA4] font-semibold hover:underline">+ Agregar al catálogo</button></p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={2} placeholder="Notas adicionales..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none resize-none"
              data-testid="oc-notes-input" />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowNew(false); setSelectedPFForOC(null); setSelectedPFIds([]); }}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
              Cancelar
            </button>
            <button onClick={handleGenerate} disabled={generating}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              data-testid="generate-oc-btn">
              {generating ? <Loader2 size={15} className="animate-spin" /> : <ShoppingCart size={15} />}
              {generating ? "Generando..." : "Generar OC"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: Editar OC */}
      {editOC && (
        <Modal open={!!editOC} onClose={() => setEditOC(null)} title={`Editar OC: ${editOC.number}`}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sucursal de retiro</label>
              {(() => {
                const { prov, sucursales } = findProvSucursales(editOC.provider_name);
                if (sucursales.length > 0) {
                  return (
                    <select value={editData.sucursal} onChange={e => setEditData(p => ({ ...p, sucursal: e.target.value }))}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                      data-testid="edit-oc-sucursal">
                      <option value="">Seleccionar sucursal...</option>
                      {sucursales.map((s, i) => <option key={i} value={s}>{s}</option>)}
                      {editData.sucursal && !sucursales.includes(editData.sucursal) && (
                        <option value={editData.sucursal}>{editData.sucursal} (actual)</option>
                      )}
                    </select>
                  );
                }
                return (
                  <input value={editData.sucursal} onChange={e => setEditData(p => ({ ...p, sucursal: e.target.value }))}
                    placeholder="Sucursal"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                    data-testid="edit-oc-sucursal" />
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Condición de pago</label>
              {(() => {
                const { prov, condiciones } = findProvCondiciones(editOC.provider_name);
                if (condiciones.length > 0) {
                  return (
                    <select value={editData.condicion_pago} onChange={e => setEditData(p => ({ ...p, condicion_pago: e.target.value }))}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                      data-testid="edit-oc-condicion">
                      <option value="">Seleccionar condición...</option>
                      {condiciones.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      {editData.condicion_pago && !condiciones.includes(editData.condicion_pago) && (
                        <option value={editData.condicion_pago}>{editData.condicion_pago} (actual)</option>
                      )}
                    </select>
                  );
                }
                return (
                  <input value={editData.condicion_pago} onChange={e => setEditData(p => ({ ...p, condicion_pago: e.target.value }))}
                    placeholder="Ej: Contado, 30 días"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                    data-testid="edit-oc-condicion" />
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
              <textarea value={editData.notes} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))}
                rows={2} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditOC(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleSaveEdit} disabled={saving}
                className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-oc-edit-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Ver OC */}
      {viewOC && (
        <Modal open={!!viewOC} onClose={() => setViewOC(null)} title={`OC: ${viewOC.number}`} extraWide>
          <div className="space-y-5">
            <div className="bg-[#2E6DA4] rounded-xl p-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium opacity-70 uppercase tracking-wider">CEN INGENIERIA E.A.S.</p>
                  <p className="text-xs opacity-60 mt-0.5">Instalaciones Eléctricas — Asunción, Paraguay</p>
                </div>
                <div className="text-right">
                  <p className="text-xs opacity-70 uppercase">Orden de Compra</p>
                  <p className="font-mono font-bold text-lg mt-0.5">{viewOC.number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3"><StatusBadge status={viewOC.status} /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "Proyecto / Obra", value: viewOC.project_name },
                { label: "Proveedor", value: viewOC.provider_name },
                { label: "Sucursal", value: viewOC.sucursal || "—" },
                { label: "Cond. de pago", value: viewOC.condicion_pago || "—" },
                { label: "PF(s) Origen", value: (viewOC.pf_numbers || []).join(", ") || "—" },
                { label: "Fecha Objetivo", value: viewOC.target_date ? formatDate(viewOC.target_date) : "—" },
                { label: "Total con IVA", value: formatPYG(viewOC.total || 0) },
                { label: "Orden de Pago", value: viewOC.payment_order_number ? `${viewOC.payment_order_number} (${viewOC.payment_status === "pagada" ? "Pagada" : "Pendiente de pago"})` : viewOC.baja_tesoreria ? `Dada de baja en Tesorería (${viewOC.baja_tesoreria.op_number})` : "Aún no generada" },   // Fix IL
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 break-words">{value}</p>
                </div>
              ))}
            </div>
            {/* Fix O (2026-08-31): historial del pedido de origen en cada paso del flujo. */}
            <HistorialPedidoCard pmId={viewOC.pm_origen_id} />
            <div>
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Ítems de la Orden <span className="text-[10px] text-gray-400 font-normal ml-1">(precios con IVA incluido)</span></h4>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#2E6DA4] text-white">
                      {["Descripción", "UM", "Cant.", "P.U. c/IVA (Gs.)", "Subtotal c/IVA (Gs.)"].map(h => (
                        <th key={h} className="text-left px-3 py-2.5 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agruparPorMaterial(viewOC.items, true).map((item, i) => {
                      // item.unit_price y item.subtotal ya vienen con IVA incluido (convención CEN)
                      const upWith = Math.round(item.unit_price || 0);
                      const subWith = Math.round(item.subtotal || 0);
                      return (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-blue-50/30"}>
                          <td className="px-3 py-2.5 font-medium text-gray-800">
                            {item.item_name || item.material_name}
                            {item.__lineas > 1 && <span className="block text-[10px] font-normal text-gray-400" data-testid={`oc-item-lineas-${i}`}>Total de {item.__lineas} ítems del presupuesto</span>}
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 text-center">{item.unit || "—"}</td>
                          <td className="px-3 py-2.5 font-bold text-gray-900 text-center">{item.quantity}</td>
                          <td className="px-3 py-2.5 text-right text-gray-700">{formatPYG(upWith)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-[#2E6DA4]">{formatPYG(subWith)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td colSpan={4} className="px-3 py-2.5 text-right text-gray-700 text-xs">TOTAL (IVA incluido):</td>
                      <td className="px-3 py-2.5 text-right text-[#2E6DA4] font-mono text-sm">{formatPYG(viewOC.total || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            {/* BLOQUE 41 — todas las facturas vinculadas a la OC */}
            {ocInvoices(viewOC).length > 0 && (
              <div data-testid="viewoc-invoices-section">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Facturas Vinculadas ({ocInvoices(viewOC).length})
                  <span className="text-[10px] text-gray-400 font-normal ml-1">
                    total facturado {formatPYG(ocCoverage(viewOC).invoiced_total || 0)} de {formatPYG(viewOC.total || 0)}
                  </span>
                  {ocCoverage(viewOC).pending_total > 0 && (
                    <span className="text-[11px] text-amber-700 font-bold ml-2 bg-amber-50 border border-amber-200 rounded-lg px-2 py-0.5" data-testid="viewoc-falta-facturar">
                      Falta facturar {formatPYG(ocCoverage(viewOC).pending_total)}
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto rounded-xl border border-emerald-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-emerald-600 text-white">
                        {["N° Factura", "Cód. interno", "Fecha", "Timbrado", "Ítems", "Monto (Gs.)"].map(h => (
                          <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-emerald-100">
                      {ocInvoices(viewOC).map((inv, i) => (
                        <tr key={inv.invoice_id || i} className={i % 2 === 0 ? "bg-white" : "bg-emerald-50/30"} data-testid={`viewoc-invoice-${i}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-gray-800">{inv.invoice_number}</td>
                          <td className="px-3 py-2 font-mono text-emerald-700">{inv.fac_number || "—"}</td>
                          <td className="px-3 py-2 text-gray-600">{inv.invoice_date ? formatDate(inv.invoice_date) : "—"}</td>
                          <td className="px-3 py-2 text-gray-600">{inv.timbrado || "—"}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{(inv.items || []).length || "—"}</td>
                          <td className="px-3 py-2 text-right font-bold text-gray-900">{formatPYG(inv.invoice_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {viewOC.notes && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Observaciones</p>
                <p className="text-sm text-blue-800 mt-1">{viewOC.notes}</p>
              </div>
            )}
            {viewOC.approved_by_name && viewOC.status !== "cancelada" && (
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100 text-xs text-emerald-700">
                Aprobado por: <span className="font-semibold">{viewOC.approved_by_name}</span> · {formatDate(viewOC.approved_at)}
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => generateOCPDF(viewOC)}
                className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                data-testid="pdf-oc-modal-btn">
                <Download size={14} /> Descargar PDF
              </button>
              <button onClick={() => setViewOC(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Configurar límite aprobación */}
      {editLimit && (
        <Modal open={editLimit} onClose={() => setEditLimit(false)} title="Configurar Límite de Aprobación">
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-700">
              <p className="font-semibold mb-1">Aprobación escalonada por monto</p>
              <p>OCs por debajo del límite se aprueban automáticamente. OCs que superen el límite requerirán aprobación del Gerente Técnico o Administrador.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Límite actual: {formatPYG(approvalLimit)}</label>
              <input type="number" value={newLimit} onChange={e => setNewLimit(e.target.value)}
                placeholder="Ingrese nuevo límite en Guaraníes"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                data-testid="limit-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditLimit(false)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleSaveLimit} disabled={savingLimit}
                className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-limit-btn">
                {savingLimit ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {savingLimit ? "Guardando..." : "Guardar Límite"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

